import { useEffect, useState } from "react";
import { Clock, Moon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KT, useKidsDarkMode } from "@/context/KidsDarkModeContext";
import { gradeLevelStorage } from "@/lib/localStorage";
import {
  USAGE_LIMIT_EVENT,
  formatResetTime,
  usageLimitCopy,
  type UsageLimitDetail,
} from "@/lib/usageLimit";

const KIDS_ACCENT = "#FF9600";
const KIDS_TINT = "rgba(255,150,0,0.16)";

/**
 * The one place a usage limit is shown. Adult accounts get the app's own dialog
 * with an upgrade route; kids accounts get a friendly kids-theme modal that
 * points them to a grown-up instead of a pricing page.
 */
interface UsageLimitModalProps {
  /** Opens the plans page; supplied by App because this renders outside the router. */
  onUpgrade: () => void;
}

export default function UsageLimitModal({ onUpgrade }: UsageLimitModalProps) {
  const [detail, setDetail] = useState<UsageLimitDetail | null>(null);
  const { isDark } = useKidsDarkMode();
  const isKids = gradeLevelStorage.isKids();

  useEffect(() => {
    const onLimit = (event: Event) => setDetail((event as CustomEvent<UsageLimitDetail>).detail);
    window.addEventListener(USAGE_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(USAGE_LIMIT_EVENT, onLimit);
  }, []);

  if (!detail) return null;
  const close = () => setDetail(null);
  const copy = usageLimitCopy(detail, isKids ? "kids" : "adult");
  const resetTime = detail.kind === "daily" ? formatResetTime(detail.resetsAt) : null;

  if (isKids) {
    return (
      <Dialog open onOpenChange={(open) => !open && close()}>
        <DialogContent
          className="max-w-sm p-6 sm:p-7"
          style={{ background: KT.card(isDark), borderColor: isDark ? "rgba(255,255,255,0.14)" : KIDS_ACCENT }}
        >
          <DialogHeader className="items-center text-center">
            <div className="mb-1 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: KIDS_TINT }}>
              <Moon className="h-8 w-8" style={{ color: KIDS_ACCENT }} aria-hidden="true" />
            </div>
            <DialogTitle className="font-sans text-xl font-black leading-snug" style={{ color: KT.heading(isDark) }}>
              {copy.title}
            </DialogTitle>
            <DialogDescription className="text-center text-base font-semibold leading-relaxed" style={{ color: KT.body(isDark) }}>
              {copy.body}
            </DialogDescription>
          </DialogHeader>
          <button
            type="button"
            autoFocus
            onClick={close}
            className="mt-3 min-h-12 rounded-2xl px-5 text-base font-black text-white"
            style={{ background: KIDS_ACCENT, boxShadow: `0 4px 0 ${isDark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.18)"}` }}
          >
            Got it
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  const canUpgrade = detail.kind !== "rate";
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-ember-soft text-ember-soft-ink">
            <Clock className="h-6 w-6" aria-hidden="true" />
          </div>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            {copy.body}
            {resetTime ? ` Your allowance is back at ${resetTime}.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex flex-col gap-2">
          {canUpgrade && (
            <Button
              onClick={() => {
                close();
                onUpgrade();
              }}
            >
              See plans
            </Button>
          )}
          <Button variant={canUpgrade ? "ghost" : "primary"} onClick={close} autoFocus={!canUpgrade}>
            {canUpgrade ? "Maybe later" : "Got it"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
